/**
 * "Delete account" — the in-app half of account closure.
 *
 * A switch, per the policy: flipping it on opens the request and shows the {grace} day
 * promise; flipping it off withdraws it while it is still pending. Everything else the card
 * renders is one of the four states the request can be in, because a screen that only knew
 * "on" and "off" would leave somebody whose request was refused staring at a switch that
 * silently snapped back.
 *
 * Shared by the vendor settings page and the pet-owner profile page — same three endpoints,
 * different `role`, which is what decides whether the storefront or the customer account is
 * the thing being closed.
 */
import { useState } from 'react';
import { AlertTriangle, Loader2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusPill } from '@/components/ui/status-pill';
import { ErrorState } from '@/components/ui/error-state';
import { Skeleton } from '@/components/ui/skeleton';
import { TextAreaField } from '@/components/text-field';
import { ApiError } from '@/lib/api';
import {
  use_cancel_deletion,
  use_my_deletion_request,
  use_request_deletion,
} from '@/features/account-deletion/hooks';
import type { DeletionRole, MyDeletionRequest, OpenCommitments } from '@/features/account-deletion/api';

function date_label(iso: string): string {
  return new Date(iso).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function err_text(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

export function AccountDeletionCard({ role }: { role: DeletionRole }) {
  const state_q = use_my_deletion_request(role);
  const request_m = use_request_deletion(role);
  const cancel_m = use_cancel_deletion(role);

  const [reason, set_reason] = useState('');
  const [arming, set_arming] = useState(false);

  const state = state_q.data;
  const request = state?.request ?? null;
  const grace_days = state?.grace_days ?? 15;
  const is_open = request?.status === 'PENDING';
  const in_grace = request?.status === 'APPROVED' && request.purged_at === null;

  const noun = role === 'vendor' ? 'vendor account' : 'account';

  function on_toggle(next: boolean) {
    if (next) {
      // Never straight through from the switch. The confirmation step is the point: this is
      // the only control on either console that ends the account.
      set_arming(true);
      return;
    }
    set_arming(false);
    if (is_open) cancel_m.mutate();
  }

  return (
    <Card data-testid="account-deletion-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Trash2 className="h-5 w-5 text-destructive" />
          Delete account
        </CardTitle>
      </CardHeader>
      <CardContent>
        {state_q.isLoading ? (
          <Skeleton className="h-24 rounded-card" />
        ) : state_q.isError ? (
          <ErrorState
            title="Couldn't load this"
            description="Please try again."
            action={
              <Button variant="secondary" size="sm" onClick={() => state_q.refetch()}>
                Retry
              </Button>
            }
          />
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-semibold">
                  {in_grace
                    ? 'Your account is being deleted'
                    : is_open
                      ? 'Deletion requested'
                      : `Delete my ${noun}`}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {in_grace
                    ? 'Contact support if you want to stop this.'
                    : is_open
                      ? 'Waiting for our team to verify and process it.'
                      : `Closes your ${noun} straight away and signs you out.`}
                </p>
              </div>
              <label className="flex shrink-0 cursor-pointer items-center gap-2">
                <span className="sr-only">Delete account</span>
                <input
                  type="checkbox"
                  role="switch"
                  className="h-5 w-5 rounded border-input accent-destructive"
                  checked={is_open || arming || in_grace}
                  // Once approved the account is closed and only support can reverse it —
                  // leaving the switch live would offer an undo that does not exist.
                  disabled={in_grace || request_m.isPending || cancel_m.isPending}
                  onChange={(e) => on_toggle(e.target.checked)}
                  data-testid="toggle-delete-account"
                />
              </label>
            </div>

            {request && <RequestState request={request} grace_days={grace_days} noun={noun} />}

            {arming && !is_open && !in_grace && (
              <ArmedPanel
                noun={noun}
                grace_days={grace_days}
                commitments={state?.open_commitments}
                reason={reason}
                on_reason={set_reason}
                pending={request_m.isPending}
                error={
                  request_m.error
                    ? err_text(request_m.error, 'Could not submit the request.')
                    : null
                }
                on_cancel={() => {
                  set_arming(false);
                  request_m.reset();
                }}
                on_confirm={() =>
                  request_m.mutate(
                    { reason: reason.trim() || undefined },
                    {
                      onSuccess: () => {
                        set_arming(false);
                        set_reason('');
                      },
                    },
                  )
                }
              />
            )}

            {cancel_m.error && (
              <p className="text-xs text-destructive" data-testid="deletion-cancel-error">
                {err_text(cancel_m.error, 'Could not withdraw the request.')}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** The banner for whichever state the latest request is in. */
function RequestState({
  request,
  grace_days,
  noun,
}: {
  request: MyDeletionRequest;
  grace_days: number;
  noun: string;
}) {
  if (request.status === 'PENDING') {
    return (
      <div
        className="rounded-button border-2 border-brand-outline-variant/30 p-3"
        data-testid="deletion-status-pending"
      >
        <StatusPill tone="warning">Pending review</StatusPill>
        <p className="mt-2 text-sm text-muted-foreground">
          Your account deletion request has been submitted. After successful verification, your
          account will be permanently deleted within {grace_days} days. During this period, you may
          contact support if you wish to cancel the deletion request.
        </p>
        <p className="mt-2 text-xs text-muted-foreground">
          Requested on {date_label(request.created_at)}. Turn the switch off to withdraw it.
        </p>
      </div>
    );
  }

  if (request.status === 'APPROVED' && request.purged_at === null) {
    return (
      <div
        className="rounded-button border-2 border-destructive p-3"
        data-testid="deletion-status-approved"
      >
        <StatusPill tone="destructive">Approved</StatusPill>
        <p className="mt-2 text-sm text-muted-foreground">
          Your {noun} has been closed
          {request.purge_after
            ? ` and will be permanently deleted on ${date_label(request.purge_after)}.`
            : ` and will be permanently deleted within ${grace_days} days.`}{' '}
          Contact support before then if this was a mistake.
        </p>
      </div>
    );
  }

  if (request.status === 'REJECTED') {
    return (
      <div
        className="rounded-button border-2 border-brand-outline-variant/30 p-3"
        data-testid="deletion-status-rejected"
      >
        <StatusPill tone="neutral">Not approved</StatusPill>
        {/* Shown verbatim: it is the whole reason the person is still here. */}
        <p className="mt-2 text-sm text-muted-foreground">
          {request.decision_note ?? 'Your request was not approved.'}
        </p>
        <p className="mt-2 text-xs text-muted-foreground">
          You can ask again once whatever is named above is sorted.
        </p>
      </div>
    );
  }

  return null;
}

/** The confirmation step between the switch and the request. */
function ArmedPanel({
  noun,
  grace_days,
  commitments,
  reason,
  on_reason,
  pending,
  error,
  on_cancel,
  on_confirm,
}: {
  noun: string;
  grace_days: number;
  commitments: OpenCommitments | undefined;
  reason: string;
  on_reason: (v: string) => void;
  pending: boolean;
  error: string | null;
  on_cancel: () => void;
  on_confirm: () => void;
}) {
  const live = (commitments?.active_bookings ?? 0) + (commitments?.active_orders ?? 0);

  return (
    <div
      className="flex flex-col gap-4 rounded-button border-2 border-destructive p-4"
      data-testid="deletion-confirm-panel"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
        <p className="text-sm text-muted-foreground">
          This closes your {noun} permanently and signs you out straight away. Your data is
          deleted within {grace_days} days, and until then support can still undo it. After that
          this number starts over as a brand-new account.
        </p>
      </div>

      {/*
       * Said before the deletion, because there is no longer an "after" to say it in: nobody
       * reviews this and nobody writes back. These are commitments the person can go and
       * finish themselves in five minutes, and this is their only chance to hear about them.
       */}
      {live > 0 && (
        <p
          className="text-sm font-medium text-destructive"
          data-testid="deletion-open-commitments"
        >
          You still have {commitments?.active_bookings ?? 0} active booking
          {commitments?.active_bookings === 1 ? '' : 's'} and {commitments?.active_orders ?? 0}{' '}
          open order{commitments?.active_orders === 1 ? '' : 's'}. Deleting now leaves those
          unfinished.
        </p>
      )}

      <TextAreaField
        label="Reason"
        name="deletion_reason"
        optional
        rows={3}
        placeholder="Tell us why you are leaving (optional)"
        value={reason}
        onChange={(e) => on_reason(e.target.value)}
        data-testid="deletion-reason"
      />

      {error && (
        <p className="text-sm text-destructive" data-testid="deletion-request-error">
          {error}
        </p>
      )}

      <div className="flex flex-wrap gap-3">
        <Button
          variant="destructive"
          size="sm"
          className="gap-2"
          disabled={pending}
          onClick={on_confirm}
          data-testid="deletion-confirm"
        >
          {pending && <Loader2 className="h-4 w-4 animate-spin" />}
          Delete my {noun}
        </Button>
        <Button variant="ghost" size="sm" disabled={pending} onClick={on_cancel}>
          Keep my account
        </Button>
      </div>
    </div>
  );
}
